import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Home,
  ListChecks,
  Loader2,
  PartyPopper,
  Plus,
  Receipt,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Modal } from '@/shared/ui/Modal';
import { Pestanas } from '@/shared/ui/Pestanas';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { FacturasPorOrigen } from '@/modules/facturacion/FacturasPorOrigen';
import { ModalDeCargo } from './ModalDeCargo';
import { AvisoDelPlazo } from './PlazoDeLaOrdenanza';
import {
  enTarifa,
  enUnidad,
  type Cargo,
  type MesDeAseoPorFacturar,
  type TarifasDeCargo,
} from './contrato';

export function PaginaCargosPuntuales() {
  const [vista, setVista] = useState<'cargos' | 'facturas'>('cargos');
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [nuevo, setNuevo] = useState(false);
  const [facturando, setFacturando] = useState<MesDeAseoPorFacturar | null>(null);
  const [borrando, setBorrando] = useState<Cargo | null>(null);
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('generadores.gestionar');

  const buscado = useValorDiferido(texto.trim());

  const tarifas = useQuery({
    queryKey: ['cargos', 'tipos'],
    queryFn: () => api.get<TarifasDeCargo>('/cargos/tipos'),
  });

  const porFacturar = useQuery({
    queryKey: ['cargos', 'por-facturar'],
    queryFn: () => api.get<MesDeAseoPorFacturar[]>('/cargos/por-facturar'),
  });

  const cargos = useQuery({
    queryKey: ['cargos', 'lista', buscado, tipo, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Cargo>>('/cargos', {
        query: { texto: buscado, tipo, pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  const borrar = useMutation({
    mutationFn: (c: Cargo) => api.delete<void>(`/cargos/${c.id}`),
    onSuccess: () => {
      notificar.exito('Cargo borrado');
      setBorrando(null);
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO BORRAR'),
  });

  function refrescar() {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['cargos'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['facturas'] });
  }

  const mesActual = hoy().slice(0, 7);
  const mesesCerrados = (porFacturar.data ?? []).filter((m) => m.periodo < mesActual);
  const enCurso = (porFacturar.data ?? []).find((m) => m.periodo === mesActual);

  const columnas: Columna<Cargo>[] = [
    {
      clave: 'fecha',
      encabezado: 'Fecha',
      clase: 'w-24',
      contenido: (c) => (
        <span className="importe text-xs text-armazon-600">{enFecha(c.fecha)}</span>
      ),
    },
    {
      clave: 'tipo',
      encabezado: 'Cargo',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase text-armazon-800">
            {c.cuentaId ? (
              <Home className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
            ) : (
              <PartyPopper className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
            )}
            <span className="truncate">
              {c.literal.toUpperCase()}) {c.tipoNombre}
            </span>
          </span>
          <span className="block truncate text-[10px] uppercase text-armazon-500">
            {c.descripcion}
          </span>
          <span className="importe block text-[10px] text-armazon-400">INFORME {c.informe}</span>
        </span>
      ),
    },
    {
      clave: 'quien',
      encabezado: 'Paga',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-semibold uppercase text-armazon-800">
            {c.nombre}
          </span>
          <span className="importe block text-[10px] text-armazon-400">
            {c.identificacion}
            {c.numeroCuenta && ` · CUENTA ${c.numeroCuenta}`}
          </span>
        </span>
      ),
    },
    {
      clave: 'cantidad',
      encabezado: 'Medido',
      alinear: 'derecha',
      contenido: (c) => (
        <span className="importe block text-xs text-armazon-700">
          {c.cantidad.toLocaleString('es-EC', { maximumFractionDigits: 3 })} {enUnidad(c.medida)}
          <span className="block text-[10px] text-armazon-400">
            ${enTarifa(c.tarifaUnitaria)}/{enUnidad(c.unidad)}
          </span>
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (c) => (
        <span className="importe text-xs font-bold text-armazon-900">${c.total.toFixed(2)}</span>
      ),
    },
    {
      clave: 'factura',
      encabezado: 'Factura',
      contenido: (c) =>
        c.facturaId ? (
          <span className="importe text-[11px] text-emerald-700">
            {c.numeroFactura ?? 'EMITIDA · SIN NUMERAR'}
          </span>
        ) : (
          <span className="text-[10px] font-semibold uppercase text-amber-700">
            Se factura con el mes
          </span>
        ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-12',
      contenido: (c) =>
        puedeGestionar &&
        !c.facturaId && (
          <BotonDeFila icono={Trash2} titulo="Borrar el cargo" alPulsar={() => setBorrando(c)} />
        ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Sparkles}
        titulo="Cargos puntuales"
        subtitulo="Aseo de espacios particulares y eventos públicos · Art. 9 literales e y f"
        acciones={
          puedeGestionar &&
          tarifas.data && (
            <button
              type="button"
              onClick={() => setNuevo(true)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700"
            >
              <Plus className="size-4" aria-hidden />
              Nuevo cargo
            </button>
          )
        }
      />

      {tarifas.data && (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {tarifas.data.tipos.map((t) => (
            <div key={t.codigo} className="rounded-2xl border border-armazon-200 bg-white p-4">
              <span className="rounded bg-armazon-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-armazon-600">
                Literal {t.literal}) · {t.porcentajeSbu.toLocaleString('es-EC')} % SBU
              </span>
              <p className="mt-2 text-xs font-bold uppercase leading-snug text-armazon-800">
                {t.nombre}
              </p>
              <p className="importe mt-2 text-xl font-bold text-marca-700">
                ${enTarifa(t.tarifaUnitaria)}
                <span className="text-xs font-semibold text-armazon-500">
                  {' '}
                  / {enUnidad(t.unidad, true)}
                </span>
              </p>
              <p className="mt-1 text-[10px] uppercase text-armazon-400">
                {t.facturacion === 'MENSUAL'
                  ? 'Al titular del predio · factura al mes'
                  : 'Al organizador · factura en el acto'}
              </p>
            </div>
          ))}
        </div>
      )}

      {mesesCerrados.map((m) => (
        <div
          key={m.periodo}
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-marca-200 bg-marca-50 p-4"
        >
          <div className="grid gap-2">
            <p className="text-sm font-bold uppercase text-marca-900">
              Aseo de {enMes(m.periodo)} por facturar
            </p>
            <p className="text-xs uppercase text-marca-800">
              {m.cargos} {m.cargos === 1 ? 'limpieza' : 'limpiezas'} · {m.titulares}{' '}
              {m.titulares === 1 ? 'titular' : 'titulares'} · ${m.total.toFixed(2)}
            </p>
            <AvisoDelPlazo periodo={m.periodo} />
          </div>
          {puedeGestionar && (
            <button
              type="button"
              onClick={() => setFacturando(m)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700"
            >
              <Receipt className="size-4" aria-hidden />
              Facturar {enMes(m.periodo)}
            </button>
          )}
        </div>
      ))}
      {enCurso && (
        <p className="mb-4 text-[11px] font-semibold uppercase text-armazon-500">
          {enCurso.cargos} {enCurso.cargos === 1 ? 'limpieza' : 'limpiezas'} de{' '}
          {enMes(enCurso.periodo)} · ${enCurso.total.toFixed(2)}: se facturan cuando termine el mes
        </p>
      )}

      <Pestanas
        pestanas={[
          { clave: 'cargos', rotulo: 'Cargos', descripcion: 'Lo registrado', icono: ListChecks },
          { clave: 'facturas', rotulo: 'Facturas', descripcion: 'Lo facturado', icono: FileText },
        ]}
        activa={vista}
        alElegir={setVista}
        etiqueta="Qué ver"
      />

      {vista === 'facturas' ? (
        <FacturasPorOrigen
          origen="CARGOS"
          situacionInicial=""
          vacio="Ninguna factura de cargos puntuales coincide"
        />
      ) : (
        <Tabla
          datos={cargos.data}
          cargando={cargos.isPending}
          columnas={columnas}
          tamanio={tamanio}
          alCambiarPagina={setPagina}
          alCambiarTamanio={(n) => {
            setTamanio(n);
            setPagina(0);
          }}
          entidad="cargos"
          iconoVacio={Sparkles}
          mensajeVacio="Ningún cargo puntual todavía"
          herramientas={
            <span className="flex flex-wrap gap-2">
              <select
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setPagina(0);
                }}
                aria-label="Tipo de cargo"
                className="h-10 rounded-lg border border-armazon-200 bg-white px-2 text-xs font-semibold uppercase text-armazon-700"
              >
                <option value="">TODOS LOS TIPOS</option>
                {tarifas.data?.tipos.map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {`${t.literal.toUpperCase()}) ${t.nombre}`}
                  </option>
                ))}
              </select>
              <CampoDeBusqueda
                valor={texto}
                alCambiar={(v) => {
                  setTexto(v);
                  setPagina(0);
                }}
                marcador="Titular, cédula, cuenta o informe…"
                className="w-72"
              />
            </span>
          }
        />
      )}

      {nuevo && tarifas.data && (
        <ModalDeCargo
          tipos={tarifas.data.tipos}
          alCerrar={() => setNuevo(false)}
          alRegistrar={() => {
            setNuevo(false);
            refrescar();
          }}
        />
      )}

      {facturando && (
        <ModalDeFacturacion
          mes={facturando}
          alCerrar={() => setFacturando(null)}
          alFacturar={() => {
            setFacturando(null);
            refrescar();
          }}
        />
      )}

      {borrando && (
        <Modal
          abierto
          alCerrar={() => setBorrando(null)}
          titulo="Borrar el cargo"
          subtitulo={`${borrando.nombre} · $${borrando.total.toFixed(2)}`}
          icono={Trash2}
          ancho="estrecho"
        >
          <div className="grid gap-4">
            <p className="text-xs uppercase leading-relaxed text-armazon-600">
              Informe {borrando.informe} · {borrando.descripcion}. Todavía no está facturado: se
              borra sin dejar rastro en la cartera.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBorrando(null)}
                className="h-10 rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-600 hover:bg-armazon-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => borrar.mutate(borrando)}
                disabled={borrar.isPending}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-bold uppercase text-white shadow-sm hover:bg-rose-700 disabled:opacity-50"
              >
                {borrar.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Borrar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function ModalDeFacturacion({
  mes,
  alCerrar,
  alFacturar,
}: {
  mes: MesDeAseoPorFacturar;
  alCerrar: () => void;
  alFacturar: () => void;
}) {
  const facturar = useMutation({
    mutationFn: () =>
      api.post<{ facturas: number; cargos: number; total: number }>(
        '/cargos/facturacion',
        undefined,
        {
          query: { periodo: mes.periodo },
        },
      ),
    onSuccess: (r) => {
      notificar.exito(
        `${r.facturas} facturas · $${r.total.toFixed(2)}`,
        `Aseo de ${enMes(mes.periodo)} facturado`,
      );
      alFacturar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO FACTURAR'),
  });

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={`Facturar el aseo de ${enMes(mes.periodo)}`}
      subtitulo="Una factura por titular, con una línea por limpieza y su predio"
      icono={Receipt}
      ancho="medio"
    >
      <div className="grid gap-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <Dato valor={String(mes.titulares)} rotulo="Facturas" />
          <Dato valor={String(mes.cargos)} rotulo="Limpiezas" />
          <Dato valor={`$${mes.total.toFixed(2)}`} rotulo="Total" />
        </div>
        <AvisoDelPlazo periodo={mes.periodo} />
        <p className="text-[11px] uppercase leading-relaxed text-armazon-500">
          Las facturas nacen pendientes de cobro, vencen según el plazo de pago configurado y salen
          al SRI con la emisión masiva.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={alCerrar}
            className="h-10 rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-600 hover:bg-armazon-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => facturar.mutate()}
            disabled={facturar.isPending}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-5 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:opacity-50"
          >
            {facturar.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
            Facturar
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Dato({ valor, rotulo }: { valor: string; rotulo: string }) {
  return (
    <div className="rounded-xl border border-armazon-200 bg-white p-3">
      <span className="importe block text-lg font-bold text-armazon-800">{valor}</span>
      <span className="text-[10px] font-semibold uppercase text-armazon-500">{rotulo}</span>
    </div>
  );
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

function enMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  return `${MESES[Number(mes) - 1] ?? mes} ${anio}`;
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

function hoy(): string {
  const d = new Date();
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}
